import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mic, Languages, Briefcase, Camera } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useApi, OFFICER_IMG } from "@/lib/api";
import { PageTitle } from "@/components/Bits";

const ROLES = [
  { key: "linguistic", label: "Linguistic / Translation / QA", icon: Languages, desc: "Training local language teams, translation review & quality assurance." },
  { key: "project", label: "Project Management / Leadership", icon: Briefcase, desc: "Project oversight, workshops, partner meetings, launches & conferences." },
  { key: "media", label: "Media / Audio-Visual", icon: Camera, desc: "Event coverage, filming & photography with professional equipment." },
];

export default function InterviewSetup() {
  const api = useApi();
  const navigate = useNavigate();
  const [role, setRole] = useState("linguistic");
  const [destination, setDestination] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [micOk, setMicOk] = useState(null);

  useEffect(() => {
    navigator.permissions?.query({ name: "microphone" }).then((p) => setMicOk(p.state)).catch(() => {});
  }, []);

  const start = async () => {
    if (!destination.trim()) return toast.error("Please enter your destination country / city");
    setBusy(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      setBusy(false);
      return toast.error("Microphone access is required for the voice interview");
    }
    try {
      const data = await api("post", "/sessions", { team_role: role, destination, trip_details: details });
      navigate(`/interview/${data.session.id}`, { state: { opening: data.officer } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the interview");
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10 fade-up" data-testid="interview-setup-page">
      <div className="lg:col-span-7">
        <PageTitle kicker="Pre-interview briefing" title="Prepare your trip file" />
        <Label className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">1 · Your team type</Label>
        <div className="grid md:grid-cols-3 gap-3 mt-3 mb-10">
          {ROLES.map((r) => (
            <button key={r.key} type="button" onClick={() => setRole(r.key)} data-testid={`role-option-${r.key}`}
              className={`text-left p-5 border transition-colors ${role === r.key ? "border-blue-500 bg-blue-600/10" : "border-slate-800 bg-slate-900 hover:border-slate-600"}`}>
              <r.icon className={`w-5 h-5 ${role === r.key ? "text-blue-400" : "text-slate-500"}`} />
              <p className="font-cabinet font-bold text-white mt-3 leading-tight">{r.label}</p>
              <p className="text-xs text-slate-400 mt-2">{r.desc}</p>
            </button>
          ))}
        </div>

        <Label htmlFor="dest" className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">2 · Destination country / city</Label>
        <Input id="dest" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. Nairobi, Kenya"
          className="mt-3 mb-10 bg-slate-900 border-slate-800 h-12 focus-visible:ring-1 focus-visible:ring-blue-500" data-testid="destination-input" />

        <Label htmlFor="details" className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">3 · Trip details (optional, used by the officer to check consistency)</Label>
        <Textarea id="details" value={details} onChange={(e) => setDetails(e.target.value)} rows={6}
          placeholder="Dates, host organisation & contact, hotel, flights & transit, who pays, activities, equipment..."
          className="mt-3 bg-slate-900 border-slate-800 focus-visible:ring-1 focus-visible:ring-blue-500" data-testid="trip-details-input" />

        <button onClick={start} disabled={busy} data-testid="begin-interview-btn"
          className="mt-10 inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white px-8 py-4 transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
          {busy ? "Officer is calling you forward..." : "Step up to the counter"}
        </button>
      </div>

      <aside className="lg:col-span-5">
        <div className="bg-slate-900 border border-slate-800 sticky top-24">
          <img src={OFFICER_IMG} alt="Officer Fredo Quist" className="w-full h-64 object-cover object-top grayscale" />
          <div className="p-8 space-y-4">
            <p className="font-cabinet font-bold text-white text-2xl">Officer Fredo Quist</p>
            <ul className="text-sm text-slate-400 space-y-3 list-none">
              <li>· The interview runs 20–30 minutes. He speaks; you answer by voice.</li>
              <li>· Hands-free: the mic opens after each question and submits when you pause for 2 seconds. You can also press the mic to stop early.</li>
              <li>· Answer the question asked — clearly, briefly, truthfully.</li>
              <li>· Never describe a work trip as tourism. Don't guess details.</li>
              <li>· Unclear question? Ask him to repeat it.</li>
            </ul>
            {micOk === "denied" && <p className="text-rose-500 text-sm" data-testid="mic-denied-warning">Microphone is blocked in your browser settings.</p>}
          </div>
        </div>
      </aside>
    </div>
  );
}
